import { useMemo } from 'react'
import { Vector3, BufferGeometry, Mesh, Line } from 'three'
import { useCommentsStore, useModelStore } from '@editorStores'
import { useDisposeThreeObjectsOnUnmount } from '@editorHooks'

/**
 * Creates snap targets from comments:
 * - Comment positions
 * - Callout end points
 * - Callout knee points (if available)
 * - Callout lines (from position to end, or position to knee to end)
 */
const useCommentSnapTargets = (xyOnly = false) => {
  const comments = useCommentsStore(state => state.comments)
  const visibleStoreys = useModelStore(state => state.visibleStoreys)
  const showComments = useModelStore(state => state.showComments)

  const snapTargets = useMemo(() => {
    // Return empty array if comments are not visible
    if (!showComments) {
      return []
    }

    // Filter comments based on visibility
    const visibleComments = comments.filter(comment => {
      // Show comment if storeyForSceneRendering is null (always visible)
      if (
        comment.storeyForSceneRendering === null ||
        comment.storeyForSceneRendering === undefined
      ) {
        return true
      }
      // Show comment if its storeyForSceneRendering is in the visible storeys
      return visibleStoreys.has(comment.storeyForSceneRendering)
    })

    const targets: (Mesh | Line)[] = []

    visibleComments.forEach(comment => {
      const position = new Vector3(comment.position.x, comment.position.y, comment.position.z)

      if (xyOnly) {
        position.setZ(0)
      }

      // Add comment position as snap target
      const positionGeometry = new BufferGeometry().setFromPoints([position])
      const positionMesh = new Mesh(positionGeometry)
      positionMesh.userData.commentId = comment.id
      positionMesh.userData.type = 'comment-position'
      targets.push(positionMesh)

      // Add callout targets if callouts exist
      if (comment.callouts && comment.callouts.length > 0) {
        comment.callouts.forEach((callout, index) => {
          const end = new Vector3(callout.end.x, callout.end.y, callout.end.z)
          if (xyOnly) {
            end.setZ(0)
          }

          // Add callout end point
          const endGeometry = new BufferGeometry().setFromPoints([end])
          const endMesh = new Mesh(endGeometry)
          endMesh.userData.commentId = comment.id
          endMesh.userData.calloutIndex = index
          endMesh.userData.type = 'callout-end'
          targets.push(endMesh)

          if (callout.knee) {
            const knee = new Vector3(callout.knee.x, callout.knee.y, callout.knee.z)
            if (xyOnly) {
              knee.setZ(0)
            }

            // Add callout knee point
            const kneeGeometry = new BufferGeometry().setFromPoints([knee])
            const kneeMesh = new Mesh(kneeGeometry)
            kneeMesh.userData.commentId = comment.id
            kneeMesh.userData.calloutIndex = index
            kneeMesh.userData.type = 'callout-knee'
            targets.push(kneeMesh)

            // Add line from position to knee
            const positionClone = position.clone()
            const kneeClone = knee.clone()
            const line1Geometry = new BufferGeometry().setFromPoints([positionClone, kneeClone])
            const line1 = new Line(line1Geometry)
            line1.userData.commentId = comment.id
            line1.userData.calloutIndex = index
            line1.userData.type = 'callout-line'
            targets.push(line1)

            // Add line from knee to end
            const endClone = end.clone()
            const line2Geometry = new BufferGeometry().setFromPoints([kneeClone, endClone])
            const line2 = new Line(line2Geometry)
            line2.userData.commentId = comment.id
            line2.userData.calloutIndex = index
            line2.userData.type = 'callout-line'
            targets.push(line2)
          } else {
            // Add line from position to end (no knee)
            const positionClone = position.clone()
            const endClone = end.clone()
            const lineGeometry = new BufferGeometry().setFromPoints([positionClone, endClone])
            const line = new Line(lineGeometry)
            line.userData.commentId = comment.id
            line.userData.calloutIndex = index
            line.userData.type = 'callout-line'
            targets.push(line)
          }
        })
      }
    })

    return targets
  }, [comments, visibleStoreys, xyOnly, showComments])

  useDisposeThreeObjectsOnUnmount(snapTargets)

  return snapTargets
}

export default useCommentSnapTargets
